import { PatchComponentsByComponentApiResponse, useGetProductVariantsQuery, useGetProductsQuery, ProductVariantResource } from '@/api/serverApi'
import { replaceAnchorSpacings, updateComponentAction, replaceCastInChannelAction } from '@/stores/active-design'
import { Button, Dropdown, LoadingSpinner, MaybeApiPayloadError, ToggleButtonGroup, Checkbox, FilledWarningIcon, TextInput } from '@cfs-fixings/shared'
import PanelTitle from './components/PanelTitle'
import PanelSections from './components/PanelSections'
import PanelSection from './components/PanelSection'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/stores'
import { ChangeEvent, useEffect, useState } from 'react'
import PanelInput from './components/PanelInput'
import * as v from 'valibot'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { nanoid } from '@reduxjs/toolkit'
import { valibotResolver } from '@hookform/resolvers/valibot'
import PanelSelect from './components/PanelSelect'
import ConfirmUnsavedChangesModal from '@/components/modals/ConfirmUnsavedChangesModal'
import ConfirmationModal from '@/components/modals/ConfirmationModal'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import ComponentType from '@/enums/ComponentType'
import PanelRangeInput from './components/PanelRangeInput'

type TriggerUpdateKey = keyof NonNullable<Parameters<typeof updateComponentAction>[0]['details']['cast_in_anchor_channel']>

const channelSchema = v.object({
  cast_in_anchor_channel: v.optional(v.object({
    length: v.optional(v.number([v.integer()])),
    custom_length: v.optional(v.number([v.integer(), v.minValue(0), v.maxValue(3050)])),
    profile_size: v.optional(v.string()),
    anchor_spacings: v.optional(v.array(v.object({ key: v.string(), value: v.number([v.integer()]) }))),
    fixing_position: v.optional(v.string()),
    has_lever_arm: v.optional(v.boolean()),
    lever_arm_distance: v.optional(v.number([v.integer()])),
    lever_arm_degree_of_restraint: v.optional(v.number([v.integer(), v.minValue(1), v.maxValue(2)])),
    has_custom_anchor_spacings: v.optional(v.boolean()),
  })),
})

type CastInAnchorChannelFormInputs = v.Input<typeof channelSchema>

const CastInAnchorChannelProperties = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [initialValuesSet, setInitialValuesSet] = useState(false)
  const [errorKeys] = useState<string[]>([])
  const [isConfirmProfileSizeOpen, setIsConfirmProfileSizeOpen] = useState(false)
  const [selectedProductVariant, setSelectedProductVariant] = useState<ProductVariantResource | null>(null)
  const [tBoltOption, setTBoltOption] = useState<any | null>(null)

  const selectedComponentId = useSelector((state: RootState) => state.activeDesign.selectedComponentId)

  const { data: products, isFetching: isFetchingProducts } = useGetProductsQuery({
    perPage: 10000,
  })

  const { data: productVariants, isFetching: isFetchingProductVariants } = useGetProductVariantsQuery({
    productType: ComponentType.CastInChannel,
    perPage: 10000,
  })

  const component = useSelector((state: RootState) => state.activeDesign.design?.components?.find(c => c.id === selectedComponentId))
  const isSaving = useSelector((state: RootState) => state.activeDesign.saving)
  const productVariant = productVariants?.data.find(p => p.id === component?.product_variant_id)
  const product = products?.data.find(p => p.id === productVariant?.product_id)

  const [selectedLengthType, setSelectedLengthType] = useState<'set' | 'custom'>(component?.cast_in_anchor_channel?.length ? 'set' : 'custom')
  const [selectedFixingPosition, setSelectedFixingPosition] = useState<'top' | 'face'>(component?.cast_in_anchor_channel?.fixing_position ?? 'top')

  const isFetching = isFetchingProducts || isFetchingProductVariants

  const { register, setValue, control, watch } = useForm<CastInAnchorChannelFormInputs>({
    resolver: valibotResolver(channelSchema),
  })

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'cast_in_anchor_channel.anchor_spacings',
  })

  useEffect(() => {
    if (initialValuesSet) {return}
    setValue('cast_in_anchor_channel.length', component?.cast_in_anchor_channel?.length ?? component?.cast_in_anchor_channel?.custom_length)
    setValue('cast_in_anchor_channel.custom_length', component?.cast_in_anchor_channel?.custom_length ?? component?.cast_in_anchor_channel?.length)
    setValue('cast_in_anchor_channel.anchor_spacings', (component?.cast_in_anchor_channel?.anchor_spacings ?? []).map(s => ({ key: nanoid(), value: s })))
    setValue('cast_in_anchor_channel.has_lever_arm', component?.cast_in_anchor_channel?.has_lever_arm)
    setValue('cast_in_anchor_channel.has_custom_anchor_spacings', component?.cast_in_anchor_channel?.has_custom_anchor_spacings)
    setValue('cast_in_anchor_channel.lever_arm_distance', component?.cast_in_anchor_channel?.lever_arm_distance ?? 1)
    setValue('cast_in_anchor_channel.lever_arm_degree_of_restraint', parseFloat(component?.cast_in_anchor_channel?.lever_arm_degree_of_restraint ?? '1'))
    setInitialValuesSet(true)
  }, [component, setValue, initialValuesSet])

  const triggerUpdate = async (key: TriggerUpdateKey, value: any) => {
    const updates = {
      [key]: value,
    }
    if (key === 'length') {
      updates.custom_length = null
    }
    if (key === 'custom_length') {
      updates.length = null
    }

    // We reset the lever arm distance on enable
    if (key === 'has_lever_arm') {
      if (value === true) {
        setValue('cast_in_anchor_channel.lever_arm_distance', 1)
        setValue('cast_in_anchor_channel.lever_arm_degree_of_restraint', 1)
        updates.lever_arm_distance = 1
        updates.lever_arm_degree_of_restraint = 1
      }
    }

    const result = await dispatch(updateComponentAction({
      componentId: selectedComponentId!,
      details: {
        cast_in_anchor_channel: updates,
      },
    }))

    const payload = result.payload as PatchComponentsByComponentApiResponse

    const possibleError = (result.payload as MaybeApiPayloadError)?.data

    if (possibleError?.errors && possibleError?.message) {
      toastHelper.dismissibleError('Invalid anchor spacing', possibleError.message)
      return
    }

    if (payload.data.cast_in_anchor_channel?.anchor_spacings) {
      dispatch(replaceAnchorSpacings(payload.data.cast_in_anchor_channel.anchor_spacings))
    }

    // Removing anchor spacings has presented problems with not
    // correctly updating in state. The easiest way
    // is just to explicitly update it, as this is the only
    // thing we need to explicitly check for validity
    if (key === 'anchor_spacings') {
      dispatch(replaceAnchorSpacings(updates.anchor_spacings))
    }

    const component = payload.data.cast_in_anchor_channel

    if (component?.anchor_spacings === undefined) {
      return
    }

    replace(component.anchor_spacings.map(s => ({ key: nanoid(), value: s })))
  }

  const toggleLengthOption = (option: 'set' | 'custom') => {
    setSelectedLengthType(option)
    const setLength = component?.cast_in_anchor_channel?.length
    const customLength = component?.cast_in_anchor_channel?.custom_length

    const lengthsSortedByClosestToCustomLength = [...productVariant?.cast_in_anchor_channel?.lengths ?? []].sort((a, b) => {
      const aDiff = Math.abs(parseInt(a.length) - customLength)
      const bDiff = Math.abs(parseInt(b.length) - customLength)
      return aDiff - bDiff
    })

    const newValue = option === 'set' && customLength
      ? lengthsSortedByClosestToCustomLength?.[0].length ?? productVariant?.cast_in_anchor_channel?.lengths[0].length
      : setLength

    triggerUpdate(
      option === 'set' ? 'length' : 'custom_length',
      newValue,
    )

    setValue('cast_in_anchor_channel.custom_length', newValue)
    setValue('cast_in_anchor_channel.length', newValue)
  }

  const toggleFixingPosition = (option: 'top' | 'face') => {
    setSelectedFixingPosition(option)
    triggerUpdate(
      'fixing_position',
      option,
    )
  }

  const [isConfirmLengthChangeOpen, setIsConfirmLengthChangeOpen] = useState(false)
  const [onConfirmLengthChange, setOnConfirmLengthChange] = useState<(() => void) | null>(null)
  const [onCancelLengthChange, setOnCancelLengthChange] = useState<(() => void) | null>(null)
  const [confirmLengthChangeMessage, setConfirmLengthChangeMessage] = useState('')
  const [shouldRefetchAnchors, setShouldRefetchAnchors] = useState(false)

  useEffect(() => {
    if (!isSaving && shouldRefetchAnchors) {
      setShouldRefetchAnchors(false)
    }
  }, [isSaving, shouldRefetchAnchors])

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner className="w-4 h-4" />
      </div>
    )
  }

  const onConfirmProfileSizeChange = async () => {
    if (!selectedProductVariant) {
      return
    }

    await dispatch(
      replaceCastInChannelAction({
        componentId: selectedComponentId!,
        body: {
          new_product_variant_id: selectedProductVariant.id,
          ...(tBoltOption && {
            t_bolt: {
              variant_id: tBoltOption.productVariant.id,
              diameter: tBoltOption.diameter?.name ?? '',
            },
          }),
        },
      }),
    )

    setIsConfirmProfileSizeOpen(false)
  }

  const onLeverArmDegreeOfRestraintRangeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.trim()

    if (value === '' || parseFloat(value) < 1) {
      return setValue('cast_in_anchor_channel.lever_arm_degree_of_restraint', 1)
    }

    if (parseFloat(value) > 2) {
      return setValue('cast_in_anchor_channel.lever_arm_degree_of_restraint', 2)
    }

    const parsedValue = parseFloat(value)

    return setValue('cast_in_anchor_channel.lever_arm_degree_of_restraint', parsedValue)
  }

  return (
    <>
      <PanelTitle>Channel</PanelTitle>

      <PanelSections>
        <PanelSection>
          <div className="flex gap-[10px] items-center">
            <img
              src={product?.image}
              alt=""
              className="w-[47px] h-[47px] object-cover rounded-[2px]"
            />

            <div>
              <h3 className="font-semibold text-[14px] text-gray-900">
                {productVariant?.name}
              </h3>
              {/* {
                product?.link && (
                  <a
                    className="font-medium text-[12px] underline text-green-900"
                    href={product?.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Product Details
                  </a>
                )
              } */}
            </div>
          </div>
        </PanelSection>

        <PanelSection title="Profile Size">
          <Controller
            control={control}
            name={'cast_in_anchor_channel.profile_size'}
            render={({ field }) => (
              <PanelSelect
                {...register('cast_in_anchor_channel.profile_size')}
                defaultValue={component?.product_variant_id}
                value={field.value}
                onChange={async (e) => {
                  const selectedVariant = productVariants?.data.find(variant => variant.id === e.target.value)
                  setSelectedProductVariant(selectedVariant || null)
                  if (selectedVariant?.compatible_children?.length) {
                    setTBoltOption({
                      productVariant: selectedVariant.compatible_children[0],
                      diameter: selectedVariant.compatible_children[0]?.t_bolt?.diameters?.[0] ?? '',
                    })
                  }
                  setIsConfirmProfileSizeOpen(true)
                }}
              >
                {productVariants?.data
                  ?.filter(variant => variant.product_type === ComponentType.CastInChannel)
                  .map(variant => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                    </option>
                  ))}
              </PanelSelect>
            )}
          />
        </PanelSection>

        <PanelSection title="Length">
          <Controller
            control={control}
            name={'cast_in_anchor_channel.length'}
            render={({ field }) => {
              const handleOnChange = (value: string) => {
                field.onChange(value)
                if (value)
                {
                  setConfirmLengthChangeMessage('Changing to a set length will reset the anchor spacings to the default for that length. Are you sure you want to change the length?')
                  setIsConfirmLengthChangeOpen(true)

                  setOnConfirmLengthChange(() => () => {
                    if (value === component?.cast_in_anchor_channel?.length) {
                      return
                    }
                    triggerUpdate('length', parseInt(value))
                    setIsConfirmLengthChangeOpen(false)
                    setShouldRefetchAnchors(true)
                  })

                  setOnCancelLengthChange(() => () => {
                    setValue('cast_in_anchor_channel.length', watch('cast_in_anchor_channel.length')  ?? component?.cast_in_anchor_channel?.length)
                    setIsConfirmLengthChangeOpen(false)
                  })
                }
              }
              return (
                <PanelSelect
                  {...register('cast_in_anchor_channel.length')}
                  value={field.value}
                  onChange={(e) => handleOnChange(e.target.value)}
                  disabled={selectedLengthType === 'custom'}
                  className="disabled:cursor-not-allowed h-[30px] !py-0 text-xs bg-gray-300 text-gray-550 font-medium"
                >
                  {
                    productVariant?.cast_in_anchor_channel?.lengths.map(length => (
                      <option key={length.length} value={length.length}>{length.length}mm</option>
                    ))
                  }
                </PanelSelect>
              )
            }}
          />
        </PanelSection>

        <PanelSection title="Fixing Position">
          <ToggleButtonGroup>
            <ToggleButtonGroup.Button
              selected={selectedFixingPosition === 'top'}
              onClick={() => toggleFixingPosition('top')}
            >
              Top
            </ToggleButtonGroup.Button>
            <ToggleButtonGroup.Button
              selected={selectedFixingPosition === 'face'}
              onClick={() => toggleFixingPosition('face')}
            >
              Face
            </ToggleButtonGroup.Button>
          </ToggleButtonGroup>
        </PanelSection>

        <PanelSection>
          <div
            className="mb-[10px] mt-[6px] flex items-center"
          >
            <Checkbox
              checked={selectedLengthType === 'custom'}
              onChange={() => toggleLengthOption(selectedLengthType === 'set' ? 'custom' : 'set')}
            />
            <span className="text-xs text-gray-600">
              Custom Length
            </span>
          </div>
          {
            selectedLengthType === 'custom'
            && (
              <>
                <PanelInput
                  title="Custom Length"
                  type="number"
                  step={1}
                  {...register('cast_in_anchor_channel.custom_length')}
                  onBlur={e => {
                    const value = parseInt(e.target.value)
                    if (value < 100 || value > 3050) {
                      toastHelper.error('Invalid Length', 'Length must be between 100 and 3050')
                      setValue('cast_in_anchor_channel.custom_length', component?.cast_in_anchor_channel?.custom_length ?? 0)
                    }
                    else {
                      triggerUpdate('custom_length', value)
                    }
                  }}
                  error={errorKeys.includes('cast_in_anchor_channel.custom_length')}
                />
                <div
                  className="mt-4 p-2 bg-red-100 text-red-600 text-xs flex justify-center items-center gap-2 rounded-md"
                >
                  <FilledWarningIcon className="h-4 w-4" />
                  <span>
                    This is not a standard product
                  </span>
                </div>
              </>
            )
          }
        </PanelSection>

        <PanelSection
          error={errorKeys.includes('cast_in_anchor_channel.anchor_spacings')}
        >
          <div
            className="mb-[15px] mt-[6px] flex items-center"
          >
            <Checkbox
              type="checkbox"
              {...register('cast_in_anchor_channel.has_custom_anchor_spacings')}
              onChange={() => triggerUpdate('has_custom_anchor_spacings', !component?.cast_in_anchor_channel?.has_custom_anchor_spacings)}
            />
            <span className="text-xs text-gray-600">
              Custom Anchor Spacing
            </span>
          </div>
          {
            component?.cast_in_anchor_channel?.has_custom_anchor_spacings
              && (
                <>
                  {
                    isSaving && shouldRefetchAnchors
                      ? (
                        <div
                          className="flex justify-center items-center w-full h-8"
                        >
                          <LoadingSpinner className="w-4 h-4" />
                        </div>
                      )
                      : (
                        <div
                          className="space-y-[20px]"
                        >
                          {
                            fields.map((field, index) => (
                              <div
                                className="flex gap-[10px] items-center"
                                key={field.id}
                              >
                                <PanelInput
                                  title={`Anchor ${index + 1}`}
                                  {...register(`cast_in_anchor_channel.anchor_spacings.${index}.value`)}
                                  onBlur={e => {
                                    const anchorSpacings = watch('cast_in_anchor_channel.anchor_spacings')?.map(s => s.value) ?? []
                                    anchorSpacings[index] = parseInt(e.target.value)
                                    triggerUpdate('anchor_spacings', anchorSpacings)
                                  }}
                                />
                                <Dropdown horizontal isDisabled={fields.length === 1}>
                                  <Dropdown.Item
                                    onClick={() => {
                                      remove(index)
                                      const anchorSpacings = watch('cast_in_anchor_channel.anchor_spacings')?.map(s => s.value) ?? []
                                      triggerUpdate('anchor_spacings', anchorSpacings)
                                    }}
                                  >
                                    Remove Anchor
                                  </Dropdown.Item>
                                </Dropdown>
                              </div>
                            ))
                          }
                          <Button
                            variant="outline"
                            size="sm"
                            className="!h-[30px]"
                            isBlock
                            onClick={() => append({ key: nanoid(), value: 0 })}
                          >
                            Add Anchor
                          </Button>
                        </div>
                      )
                  }
                </>
              )
          }
        </PanelSection>

        <PanelSection>
          <div className="flex flex-col gap-y-4">
            <div className="mb-[15px] mt-[6px] flex items-center">
              <Checkbox
                {...register('cast_in_anchor_channel.has_lever_arm')}
                onChange={() => triggerUpdate('has_lever_arm', !component?.cast_in_anchor_channel?.has_lever_arm)}
              />
              <span className="text-xs text-gray-600">
                With Lever Arm
              </span>
            </div>
            {
              component?.cast_in_anchor_channel?.has_lever_arm
                ? (
                  <>
                    <PanelInput
                      title="Lever Arm"
                      units="mm"
                      {...register('cast_in_anchor_channel.lever_arm_distance')}
                      defaultValue={1}
                      min={1}
                      onBlur={(e) => {triggerUpdate('lever_arm_distance', parseFloat(e.target.value))}}
                    />
                    <div>
                      <h3 className="flex items-center font-medium text-xs leading-[12.6px] text-gray-650">
                        Degree of Restraint
                      </h3>
                      <div className="flex w-full gap-x-4 justify-between">
                        <div className="max-w-[105px] w-full">
                          <PanelRangeInput
                            {...register('cast_in_anchor_channel.lever_arm_degree_of_restraint')}
                            onMouseUp={(e) => triggerUpdate('lever_arm_degree_of_restraint', e.currentTarget.value)}
                            max={2.0}
                            min={1.0}
                            step={0.1}
                          />
                        </div>
                        <div className="w-full max-w-[100px]">
                          <TextInput
                            variant="xs"
                            type="number"
                            value={watch('cast_in_anchor_channel.lever_arm_degree_of_restraint')}
                            onBlur={(e) => triggerUpdate('lever_arm_degree_of_restraint', e.target.value)}
                            onChange={onLeverArmDegreeOfRestraintRangeChange}
                            defaultValue={component?.cast_in_anchor_channel?.lever_arm_degree_of_restraint}
                            min={1.0}
                            max={2.0}
                            step={0.1}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )
                : null
            }
          </div>
        </PanelSection>

      </PanelSections>

      <ConfirmUnsavedChangesModal
        title="Change Length"
        message={confirmLengthChangeMessage}
        isOpen={isConfirmLengthChangeOpen}
        onConfirm={() => onConfirmLengthChange?.()}
        onClose={() => onCancelLengthChange?.()}
      />

      <ConfirmationModal
        isOpen={isConfirmProfileSizeOpen}
        onClose={() => setIsConfirmProfileSizeOpen(false)}
        onConfirm={onConfirmProfileSizeChange}
        title="Confirm Profile Size Change"
        message="Are you sure you want to change the profile size?"
      />
    </>
  )
}

export default CastInAnchorChannelProperties
